import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthState } from '@/hooks/useAuth'
import Layout from '@/components/Layout'
import { getUserAccessLevel } from '@/lib/multiOrgHelpers'
import type { AccessLevel } from '@/types'

// Route-level code splitting: elke pagina wordt een aparte chunk die pas
// ingeladen wordt wanneer de gebruiker die route bezoekt.
const Login = lazy(() => import('@/pages/Login'))
const Home = lazy(() => import('@/pages/Home'))
const MyCertificates = lazy(() => import('@/pages/MyCertificates'))
const IssueBadge = lazy(() => import('@/pages/IssueBadge'))
const BulkIssue = lazy(() => import('@/pages/BulkIssue'))
const Admin = lazy(() => import('@/pages/Admin'))
const ViewBadge = lazy(() => import('@/pages/ViewBadge'))
const Profile = lazy(() => import('@/pages/Profile'))
const ClaimVerify = lazy(() => import('@/pages/ClaimVerify'))
const Unauthorized = lazy(() => import('@/pages/Unauthorized'))
const Trainings = lazy(() => import('@/pages/Trainings'))
const TrainingDetail = lazy(() => import('@/pages/TrainingDetail'))
const PublicTrainings = lazy(() => import('@/pages/PublicTrainings'))
const PublicTrainingDetail = lazy(() => import('@/pages/PublicTrainingDetail'))
const SwpPublicPage = lazy(() => import('@/pages/SwpPublicPage'))
const MijnABCPublicPage = lazy(() => import('@/pages/MijnABCPublicPage'))
const MyRegistrations = lazy(() => import('@/pages/MyRegistrations'))
const ScanLanding = lazy(() => import('@/pages/ScanLanding'))
const TrainingParticipants = lazy(() => import('@/pages/TrainingParticipants'))
const RequestTraining = lazy(() => import('@/pages/RequestTraining'))
const MyRequests = lazy(() => import('@/pages/MyRequests'))
const EvaluateTraining = lazy(() => import('@/pages/EvaluateTraining'))
const ExitTicket = lazy(() => import('@/pages/ExitTicket'))
const Rapportage = lazy(() => import('@/pages/Rapportage'))
const VerifyAttestation = lazy(() => import('@/pages/VerifyAttestation'))
const IntakeVragenInvullen = lazy(() => import('@/pages/IntakeVragenInvullen'))
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'))
const WachtwoordInstellen = lazy(() => import('@/pages/WachtwoordInstellen'))
const SnapScan = lazy(() => import('@/pages/SnapScan'))
const SnapScanAnalyse = lazy(() => import('@/pages/SnapScanAnalyse'))
const SnapScanDirectie = lazy(() => import('@/pages/SnapScanDirectie'))
const SnapScanDirectieAnalyse = lazy(() => import('@/pages/SnapScanDirectieAnalyse'))
const LesbezoekPage = lazy(() => import('@/pages/Lesbezoek'))
const SchoolleiderEvaluatiePage = lazy(() => import('@/pages/SchoolleiderEvaluatie'))
const SchoolleiderEvaluatieDetail = lazy(() => import('@/pages/SchoolleiderEvaluatieDetail'))
const SchoolleiderBevragingInvullen = lazy(() => import('@/pages/SchoolleiderBevragingInvullen'))
const SchoolleiderPersoneelsZelfscoreInvullen = lazy(() => import('@/pages/SchoolleiderPersoneelsZelfscoreInvullen'))
const SchoolleiderPersoneelsBevragingInvullen = lazy(() => import('@/pages/SchoolleiderPersoneelsBevragingInvullen'))
const LesbezoekForm = lazy(() => import('@/components/LesbezoekForm'))
const LesbezoekVerslag = lazy(() => import('@/components/LesbezoekVerslag'))
const LesbezoekHandleiding = lazy(() => import('@/components/LesbezoekHandleiding'))
const VerslagenPage = lazy(() => import('@/pages/Verslagen'))
const VerslagForm = lazy(() => import('@/components/VerslagForm'))
const VerslagBekijken = lazy(() => import('@/components/VerslagBekijken'))
const AgendaForm = lazy(() => import('@/components/AgendaForm'))
const AgendaBekijken = lazy(() => import('@/components/AgendaBekijken'))
const DienstbrievenPage = lazy(() => import('@/pages/Dienstbrieven'))
const DienstbriefForm = lazy(() => import('@/components/DienstbriefForm'))
const DienstbriefBekijken = lazy(() => import('@/components/DienstbriefBekijken'))
const Go2030 = lazy(() => import('@/pages/Go2030'))
const MijnBSA = lazy(() => import('@/pages/MijnBSA'))
const MijnLeerlingenaantallen = lazy(() => import('@/pages/MijnLeerlingenaantallen'))
const MijnLeerlingenstromen = lazy(() => import('@/pages/MijnLeerlingenstromen'))
const MijnLeerlingenherkomst = lazy(() => import('@/pages/MijnLeerlingenherkomst'))
const MijnRegionaalAanbod = lazy(() => import('@/pages/MijnRegionaalAanbod'))
const MijnGOTo = lazy(() => import('@/pages/MijnGOTo'))
const MijnProfessionalisering = lazy(() => import('@/pages/MijnProfessionalisering'))
const MijnSchool = lazy(() => import('@/pages/MijnSchool'))
const MijnPlatform = lazy(() => import('@/pages/MijnPlatform'))
const SchoolProfessionalisering = lazy(() => import('@/pages/SchoolProfessionalisering'))
const SchoolGebruikersbeheer = lazy(() => import('@/pages/SchoolGebruikersbeheer'))
const SopPage = lazy(() => import('@/pages/Sop'))
const SwpPage = lazy(() => import('@/pages/Swp'))
const MijnABC = lazy(() => import('@/pages/MijnABC'))
const MijnABCDetail = lazy(() => import('@/pages/MijnABCDetail'))
const MijnVakdossier = lazy(() => import('@/pages/MijnVakdossier'))
const MijnVakdossierDetail = lazy(() => import('@/pages/MijnVakdossierDetail'))
const MijnSelfAssessments = lazy(() => import('@/pages/MijnSelfAssessments'))
const MijnSelfAssessmentDetail = lazy(() => import('@/pages/MijnSelfAssessmentDetail'))
const SwpCategoriePage = lazy(() => import('@/pages/SwpCategorie'))
const AvgRegister = lazy(() => import('@/pages/AvgRegister'))
const DatalekMelden = lazy(() => import('@/pages/DatalekMelden'))
const MijnBvh = lazy(() => import('@/pages/MijnBvh'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-muted-foreground">Laden...</p>
    </div>
  )
}

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredLevel?: AccessLevel
}

function ProtectedRoute({ children, requiredLevel }: ProtectedRouteProps) {
  const { user, userProfile, loading } = useAuthState()
  const location = useLocation()

  if (loading) return <PageLoader />

  if (!user) {
    const returnUrl = location.pathname + location.search
    return <Navigate to={`/login?from=${encodeURIComponent(returnUrl)}`} replace />
  }

  const accessLevel = getUserAccessLevel(userProfile)

  if (requiredLevel === 'platform-admin' && accessLevel !== 'platform-admin') {
    return <Navigate to="/unauthorized" replace />
  }

  if (requiredLevel === 'issuer' && !['platform-admin', 'group-admin', 'school-admin', 'nascholingsbeheer', 'issuer'].includes(accessLevel)) {
    return <Navigate to="/unauthorized" replace />
  }

  if (requiredLevel === 'nascholingsbeheer' && !['platform-admin', 'group-admin', 'school-admin', 'nascholingsbeheer'].includes(accessLevel)) {
    return <Navigate to="/unauthorized" replace />
  }

  if (requiredLevel === 'gebruikersbeheer' && !['platform-admin', 'group-admin', 'school-admin', 'gebruikersbeheer'].includes(accessLevel)) {
    return <Navigate to="/unauthorized" replace />
  }

  if (requiredLevel === 'school-admin' && !['platform-admin', 'group-admin', 'school-admin'].includes(accessLevel)) {
    return <Navigate to="/unauthorized" replace />
  }

  // Privé-walletaccount (badges/attesten na vertrek, zie accountLinking.ts'
  // verifyEmailLink): een apart, beperkt account zonder enige organisatie —
  // toegang hier hard beperkt tot Mijn certificaten + Profiel, los van welke
  // requiredLevel een route verder zou toestaan.
  if (userProfile?.isPriveWalletAccount && !['/mijn-certificaten', '/profiel'].includes(location.pathname)) {
    return <Navigate to="/mijn-certificaten" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Publieke routes ZONDER layout (voor externe users en iframe embedding) */}
          <Route path="/public/nascholingen" element={<PublicTrainings />} />
          <Route path="/public/nascholingen/:id" element={<PublicTrainingDetail />} />
          <Route path="/p/swp/:slug" element={<SwpPublicPage />} />
          <Route path="/p/abc/:slug" element={<MijnABCPublicPage />} />

          {/* Publieke routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/badge/:id" element={<ViewBadge />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/scan" element={<ScanLanding />} />
          <Route path="/verify-attest" element={<VerifyAttestation />} />
          <Route path="/intake-invullen" element={<IntakeVragenInvullen />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/wachtwoord-instellen" element={<WachtwoordInstellen />} />

          {/* Beschermde routes — gedeelde Layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Home />} />
            <Route path="nascholingen" element={<Trainings />} />
            <Route path="nascholingen/:id" element={<TrainingDetail />} />
            <Route path="mijn-certificaten" element={<MyCertificates />} />
            <Route path="profiel" element={<Profile />} />
            <Route path="claim-verify" element={<ClaimVerify />} />
            <Route path="mijn-nascholingen" element={<MyRegistrations />} />
            <Route path="nascholing-aanvragen" element={<RequestTraining />} />
            <Route path="mijn-aanvragen" element={<MyRequests />} />
            <Route path="nascholing-evalueren" element={<EvaluateTraining />} />
            <Route path="nascholing-exitticket" element={<ExitTicket />} />
            <Route path="snapscan" element={<SnapScan />} />
            <Route path="snapscan-analyse" element={<SnapScanAnalyse />} />
            <Route path="snapscan-directie" element={<SnapScanDirectie />} />
            <Route path="snapscan-directie-analyse" element={<SnapScanDirectieAnalyse />} />
            <Route path="lesbezoeken" element={<LesbezoekPage />} />
            <Route path="lesbezoeken/handleiding" element={<LesbezoekHandleiding />} />
            <Route path="lesbezoeken/nieuw" element={<LesbezoekForm />} />
            <Route path="lesbezoeken/:id" element={<LesbezoekForm />} />
            <Route path="lesbezoeken/:id/verslag" element={<LesbezoekVerslag />} />
            <Route path="schoolleider-evaluatie" element={<SchoolleiderEvaluatiePage />} />
            <Route path="schoolleider-evaluatie/nieuw" element={<SchoolleiderEvaluatieDetail />} />
            <Route path="schoolleider-evaluatie/:id" element={<SchoolleiderEvaluatieDetail />} />
            <Route path="schoolleider-bevraging/:bevragingId" element={<SchoolleiderBevragingInvullen />} />
            <Route path="schoolleider-personeelsbevraging-zelfscore/:bevragingId" element={<SchoolleiderPersoneelsZelfscoreInvullen />} />
            <Route path="schoolleider-personeelsbevraging/:bevragingId" element={<SchoolleiderPersoneelsBevragingInvullen />} />
            <Route path="verslagen" element={<VerslagenPage />} />
            <Route path="verslagen/nieuw" element={<VerslagForm />} />
            <Route path="verslagen/:id" element={<VerslagForm />} />
            <Route path="verslagen/:id/bekijken" element={<VerslagBekijken />} />
            <Route path="dienstbrieven" element={<DienstbrievenPage />} />
            <Route path="dienstbrieven/nieuw" element={<DienstbriefForm />} />
            <Route path="dienstbrieven/:id" element={<DienstbriefForm />} />
            <Route path="dienstbrieven/:id/bekijken" element={<DienstbriefBekijken />} />
            <Route path="agendas/nieuw" element={<AgendaForm />} />
            <Route path="agendas/:id" element={<AgendaForm />} />
            <Route path="agendas/:id/bekijken" element={<AgendaBekijken />} />

            {/* Hub-pagina's (toegankelijk voor iedereen) */}
            <Route path="mijn-bsa" element={<MijnBSA />} />
            <Route path="leerlingenaantallen" element={
                <ProtectedRoute requiredLevel="school-admin">
                  <MijnLeerlingenaantallen />
                </ProtectedRoute>
              }
            />
            <Route path="leerlingenstromen" element={
                <ProtectedRoute requiredLevel="school-admin">
                  <MijnLeerlingenstromen />
                </ProtectedRoute>
              }
            />
            <Route path="leerlingenherkomst" element={
                <ProtectedRoute requiredLevel="school-admin">
                  <MijnLeerlingenherkomst />
                </ProtectedRoute>
              }
            />
            <Route path="regionaal-aanbod" element={
                <ProtectedRoute requiredLevel="school-admin">
                  <MijnRegionaalAanbod />
                </ProtectedRoute>
              }
            />
            <Route path="mijn-goto" element={<MijnGOTo />} />
            <Route path="mijn-professionalisering" element={<MijnProfessionalisering />} />
            <Route path="mijn-school" element={<MijnSchool />} />

            <Route path="sop" element={
                <ProtectedRoute requiredLevel="school-admin">
                  <SopPage />
                </ProtectedRoute>
              }
            />

            <Route path="swp" element={
                <ProtectedRoute requiredLevel="school-admin">
                  <SwpPage />
                </ProtectedRoute>
              }
            />

            <Route path="swp/:categorieSlug" element={
                <ProtectedRoute requiredLevel="school-admin">
                  <SwpCategoriePage />
                </ProtectedRoute>
              }
            />

            <Route path="avg-register" element={
                <ProtectedRoute>
                  <AvgRegister />
                </ProtectedRoute>
              }
            />

            <Route path="datalek-melden" element={
                <ProtectedRoute>
                  <DatalekMelden />
                </ProtectedRoute>
              }
            />

            <Route path="mijn-bvh" element={
                <ProtectedRoute>
                  <MijnBvh />
                </ProtectedRoute>
              }
            />

            <Route path="mijn-abc" element={<MijnABC />} />
            <Route path="mijn-abc/:id" element={<MijnABCDetail />} />
            <Route path="mijn-vakdossier" element={<MijnVakdossier />} />
            <Route path="mijn-vakdossier/:id" element={<MijnVakdossierDetail />} />
            <Route path="self-assessments" element={<MijnSelfAssessments />} />
            <Route path="self-assessments/:id" element={<MijnSelfAssessmentDetail />} />

            <Route
              path="go2030"
              element={
                <ProtectedRoute requiredLevel="school-admin">
                  <Go2030 />
                </ProtectedRoute>
              }
            />

            <Route
              path="badge-uitreiking"
              element={
                <ProtectedRoute requiredLevel="issuer">
                  <IssueBadge />
                </ProtectedRoute>
              }
            />
            <Route
              path="bulk-uitreiking"
              element={
                <ProtectedRoute requiredLevel="issuer">
                  <BulkIssue />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin"
              element={
                <ProtectedRoute requiredLevel="platform-admin">
                  <Admin />
                </ProtectedRoute>
              }
            />

            {/* Nieuwe school-admin pagina's */}
            <Route
              path="mijn-platform"
              element={
                <ProtectedRoute requiredLevel="school-admin">
                  <MijnPlatform />
                </ProtectedRoute>
              }
            />
            <Route
              path="school-professionalisering"
              element={
                <ProtectedRoute requiredLevel="nascholingsbeheer">
                  <SchoolProfessionalisering />
                </ProtectedRoute>
              }
            />
            <Route
              path="school-gebruikersbeheer"
              element={
                <ProtectedRoute requiredLevel="gebruikersbeheer">
                  <SchoolGebruikersbeheer />
                </ProtectedRoute>
              }
            />

            {/* Backward compat: /school-admin redirect naar /mijn-platform */}
            <Route
              path="school-admin"
              element={<Navigate to="/mijn-platform" replace />}
            />

            <Route
              path="rapportage"
              element={
                <ProtectedRoute requiredLevel="school-admin">
                  <Rapportage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/trainings/:id/participants"
              element={
                <ProtectedRoute requiredLevel="nascholingsbeheer">
                  <TrainingParticipants />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
